import React, { useEffect, useRef, useState } from "react";
import styles from "./controlUnit.module.css";
import { FaStop, FaPlay, FaPause } from "react-icons/fa";
import Slider from "../slider/Slider";

// playBackRate should be between 0.5 and 4
/*
fftSize
Must be a power of 2 between 2^5 and 2^15, so one of: 
32, 64, 128, 256, 512, 1024, 2048, 4096, 8192, 16384, and 32768. Defaults to 2048.
*/
export const fftSize = 32;
export const totalBands = fftSize / 2 - 1;
const volume = 1;

export default function ControlUnit({
  audioSource,
  currSeconds = 0,
  setCurrSeconds,
  totalSeconds = 0,
  setTotalSeconds,
  progress,
  onProgressUpdate,
  isPlaying,
  setIsPlaying,
  setAmpVals,
}) {
  const [playSpeed, setPlaySpeed] = useState(1);

  const requestRef = useRef();
  const audioDataRef = useRef(null);
  const audioFileRef = useRef(null);
  const gainNode = useRef(null);
  const audioCtxRef = useRef(null);

  const cleanUp = () => {
    cancelAnimationFrame(requestRef.current);

    if (audioFileRef.current) {
      audioFileRef.current.pause();
    }

    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      try {
        audioCtxRef.current.close();
        // setTotalSeconds(0);
        // setPlaySpeed(1);
        // setCurrSeconds(0);
        setAmpVals([...Array(totalBands).fill(0)]);
        audioFileRef.current = null;
      } catch (e) {
        console.log("could not close audio context >e: ", e);
      }
    }
  };

  // RUN CLEANUP ON NEW URL...
  useEffect(() => {
    cleanUp();
    // eslint-disable-next-line
  }, [audioSource]);
  // ... AND UNMOUNT
  useEffect(() => {
    return () => cleanUp();
    // eslint-disable-next-line
  }, []);

  const init = (startPlaying = false) => {
    audioFileRef.current = new Audio();
    audioFileRef.current.crossOrigin = "Anonymous";
    audioCtxRef.current = new AudioContext();
    audioCtxRef.current.crossOrigin = "Anonymous";
    const source = audioCtxRef.current.createMediaElementSource(
      audioFileRef.current
    );

    const analyser = audioCtxRef.current.createAnalyser();
    audioFileRef.current.src = audioSource;
    // audioFileRef.current.src =
    //   "https://firebasestorage.googleapis.com/v0/b/mythmakerextrapo-1611930572726.appspot.com/o/the-wient.mp3?alt=media&token=e8b6a9fc-8f4a-4e6c-a6a7-1927880381dc";
    analyser.fftSize = fftSize;

    gainNode.current = audioCtxRef.current.createGain(); // Create a gainNode reference.
    gainNode.current.connect(audioCtxRef.current.destination);
    source.connect(gainNode.current);

    gainNode.current.gain.value = volume;

    source.connect(analyser);
    audioDataRef.current = analyser;

    if (startPlaying) {
      const playPromise = audioFileRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => console.log("playback started"))
          .catch(function (error) {
            // Automatic playback failed.
            // Show a UI element to let the user manually start playback.
            console.log("error: ", error);
          });
      }
    }

    // audioFileRef.current.play();
    requestRef.current = requestAnimationFrame(runSpectrum);
  };

  useEffect(() => {
    if (!audioFileRef.current) return;
    if (audioFileRef.current.currentTime !== currSeconds) {
      audioFileRef.current.currentTime = currSeconds;
    }
  }, [currSeconds]);

  // listen for volume change to set gain
  // useEffect(() => {
  //   if (!gainNode.current) return;
  //   gainNode.current.gain.value = volume;
  // }, [volume]);

  // listen for speed change to set gain
  useEffect(() => {
    if (!audioFileRef.current) return;

    try {
      audioFileRef.current.playbackRate = playSpeed;
    } catch (error) {
      console.log("Playback Rate not supported> Error: " + error.message);
    }
  }, [playSpeed]);

  // stop when it gets to the end
  useEffect(() => {
    if (currSeconds >= totalSeconds) {
      setIsPlaying(false);
    }
    // eslint-disable-next-line
  }, [currSeconds, totalSeconds]);

  // set animation loop
  function runSpectrum() {
    if (!audioFileRef.current) return;

    if (totalSeconds === 0) {
      setTotalSeconds(audioFileRef.current.duration);
    }

    setCurrSeconds(audioFileRef.current.currentTime);

    const bufferLength = audioDataRef.current.frequencyBinCount;
    const newAmplitudeData = new Uint8Array(bufferLength);
    audioDataRef.current.getByteFrequencyData(newAmplitudeData);

    const arr = Array.from(newAmplitudeData).slice(0, totalBands);

    // normalise
    const normalisedArr = arr.map((av) => av / 256);

    setAmpVals(normalisedArr);

    requestRef.current = requestAnimationFrame(runSpectrum);
  }

  useEffect(() => {
    if (!audioSource) return;

    if (isPlaying) {
      if (!audioFileRef.current) {
        init();
      }
      audioFileRef.current.play();
    } else if (audioFileRef.current) {
      audioFileRef.current.pause();
    }
    // eslint-disable-next-line
  }, [isPlaying]);

  const onPlayPauseToggle = () => setIsPlaying(!isPlaying);

  const onReset = () => {
    if (audioFileRef.current) {
      audioFileRef.current.pause();
    }
    setIsPlaying(false);
    setCurrSeconds(0);
  };

  const onScrub = (e) => {
    const fraction = parseFloat(e.target.value);
    onProgressUpdate(fraction);

    if (!audioFileRef.current) {
      init(false);
    } else if (totalSeconds > 0) {
      setCurrSeconds(totalSeconds * fraction);
    }
  };

  return (
    <div className={styles.controls}>
      <div className={styles.innerPanel}>
        <div className={`${styles.section} ${styles.playPauseSection}`}>
          <button className={styles.butt} onClick={onReset}>
            <FaStop />
          </button>
          <button className={styles.butt} onClick={onPlayPauseToggle}>
            {isPlaying ? <FaPause /> : <FaPlay className={styles.playIcon} />}
          </button>
        </div>

        <div className={`${styles.section} ${styles.sliderSection}`}>
          {/* <p className={styles.label}>FIND</p> */}

          <Slider
            className={styles.slider}
            min={0}
            step={0.01}
            max={1}
            value={progress}
            onChange={onScrub}
          />
        </div>

        <div className={`${styles.section} ${styles.speedSection}`}>
          {/* <p className={styles.label}>SPEED</p> */}

          <div className={styles.speedButtOuter}>
            <button
              className={`${styles.butt} ${
                playSpeed === 0.5 && styles.buttSelected
              }`}
              onClick={() => setPlaySpeed(0.5)}
            >
              ½
            </button>
          </div>
          <div
            className={`${styles.speedButtOuter} ${styles.selectedSpeedButtOuter}`}
          >
            <button
              className={`${styles.butt} ${
                playSpeed === 1 && styles.buttSelected
              }`}
              onClick={() => setPlaySpeed(1)}
            >
              1
            </button>
          </div>

          <div className={styles.speedButtOuter}>
            <button
              className={`${styles.butt} ${
                playSpeed === 2 && styles.buttSelected
              }`}
              onClick={() => setPlaySpeed(2)}
            >
              2x
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
